import type { Api } from "@reduxjs/toolkit/query/react";

import type { Any } from "@/interfaces/globals";

/**
 * Business rule:
 * Any endpoint error should prevent cache invalidation for endpoints
 * using static invalidation arrays.
 *
 * @param error Endpoint error returned by RTK Query.
 * @returns Whether cache invalidation should be skipped.
 */
const shouldSkipInvalidation = (error: unknown): boolean => {
  return Boolean(error);
};

/**
 * Patches an RTK Query API instance so that static `invalidatesTags`
 * arrays are ignored when a mutation/query returns an error.
 *
 * Functional `invalidatesTags` callbacks are intentionally left untouched,
 * since they already receive the `error` argument and can implement
 * their own invalidation logic.
 *
 * @param api RTK Query API instance to patch.
 */
export const stopCacheInvalidationOnErrorResponses = (
  api: Api<Any, Record<string, Any>, string, string, Any>,
): void => {
  const defaultInjectEndpoints = api.injectEndpoints;

  api.injectEndpoints = (options: Any): Any => {
    return defaultInjectEndpoints({
      overrideExisting: options.overrideExisting,

      endpoints: (builder: Any) => {
        const definitions = options.endpoints(builder);

        const patchedDefinitions: Record<string, Any> = {};

        for (const endpointName in definitions) {
          const endpointDefinition = definitions[endpointName];

          if (
            !endpointDefinition ||
            typeof endpointDefinition !== "object" ||
            !("invalidatesTags" in endpointDefinition)
          ) {
            patchedDefinitions[endpointName] = endpointDefinition;
            continue;
          }

          const invalidatesTags = endpointDefinition.invalidatesTags;

          /**
           * Functional invalidation handlers already receive
           * the error argument and should decide invalidation themselves.
           */
          if (typeof invalidatesTags === "function") {
            patchedDefinitions[endpointName] = endpointDefinition;
            continue;
          }

          /**
           * Convert static invalidation arrays into a callback
           * so invalidation can be skipped on errors.
           */
          patchedDefinitions[endpointName] = {
            ...endpointDefinition,

            invalidatesTags: (
              _result: unknown,
              error: unknown,
            ): typeof invalidatesTags => {
              if (shouldSkipInvalidation(error)) {
                return [];
              }

              return invalidatesTags;
            },
          };
        }

        return patchedDefinitions;
      },
    });
  };
};
